import styled, { keyframes } from "styled-components";

import { fontSizes, lineHeights } from "../../shared/styles/typography";
import { pageRoot } from "../../styles/pageRoot";

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const Container = styled.div`
  ${pageRoot}
  /* #login-page vence .app-shell > * { flex-direction: column } do GlobalStyle */
  &#login-page {
    flex-direction: row;
    align-items: stretch;
    overflow: hidden;
    min-height: 100dvh;
    height: 100%;
  }

  @media (max-width: 900px) {
    &#login-page {
      flex-direction: column;
      overflow-y: auto;
      height: auto;
    }
  }
`;

export const SideContent = styled.div`
  flex: 1.1;
  min-width: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  background: linear-gradient(
    135deg,
    var(--color-primary-dark),
    var(--color-primary-lighter)
  );
  color: #ffffff;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    width: 600px;
    height: 600px;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0) 70%
    );
    top: -100px;
    left: -100px;
    border-radius: 50%;
  }

  @media (max-width: 900px) {
    flex: none;
    min-height: 360px;
    padding: 3.2rem 2rem;
  }
`;

export const HeroImage = styled.img`
  width: 100%;
  max-width: 420px;
  position: relative;
  z-index: 2;
  animation: ${float} 6s ease-in-out infinite;
  filter: drop-shadow(0 20px 30px rgba(0, 0, 0, 0.2));

  @media (max-width: 900px) {
    max-width: 280px;
  }
`;

export const HeroText = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  margin-top: 5rem;
  animation: ${fadeIn} 1.5s ease-out;

  h1 {
    font-family: Archivo, sans-serif;
    font-weight: var(--font-weight-extrabold);
    font-size: ${fontSizes["5xl"]}px;
    line-height: ${lineHeights["5xl"]}px;
    color: #ffffff;
    margin-bottom: 1.6rem;
    letter-spacing: -1px;
    text-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  p {
    font-size: ${fontSizes.base}px;
    line-height: ${lineHeights.base}px;
    color: rgba(255, 255, 255, 0.9);
    max-width: 440px;
    margin: 0 auto;
  }

  @media (max-width: 900px) {
    margin-top: 3.2rem;
    h1 {
      font-size: ${fontSizes["3xl"]}px;
      line-height: ${lineHeights["3xl"]}px;
    }
    p {
      font-size: ${fontSizes.base}px;
      line-height: ${lineHeights.base}px;
    }
  }
`;

export const FormContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  background: ${({ theme }) => theme.colors.card};
  overflow-y: auto;

  @media (max-width: 900px) {
    flex: none;
    padding: 3.2rem 2rem 4rem;
  }
`;

export const GlassCard = styled.div`
  width: 100%;
  max-width: 440px;
  background: transparent;
  padding: 2rem 0;
  animation: ${slideInRight} 0.6s cubic-bezier(0.16, 1, 0.3, 1);

  header {
    margin-bottom: 4.8rem;
    text-align: left;

    img {
      height: 42px;
      margin-bottom: 3.2rem;
    }

    h2 {
      font-family: Archivo, sans-serif;
      font-weight: var(--font-weight-extrabold);
      font-size: ${fontSizes["3xl"]}px;
      line-height: ${lineHeights["3xl"]}px;
      color: var(--color-text-title);
      margin-bottom: 1.2rem;
      letter-spacing: -1px;
    }

    p {
      color: var(--color-text-complement);
      font-size: ${fontSizes.base}px;
      line-height: ${lineHeights.base}px;
    }
  }

  .ant-form-item-label > label {
    font-weight: var(--font-weight-semibold);
    color: var(--color-text-title);
    font-size: ${fontSizes.sm}px;
    line-height: ${lineHeights.sm}px;
  }

  .ant-input-affix-wrapper {
    padding: 1.6rem 1.8rem;
    background: #ffffff;
    border: 2px solid #eaecef;
    border-radius: 12px;
    transition: all 0.2s ease;

    &:hover,
    &-focused {
      border-color: var(--color-primary);
      box-shadow: 0 4px 12px rgba(70, 20, 187, 0.05);
    }
  }

  .ant-input {
    font-size: ${fontSizes.base}px;
    line-height: ${lineHeights.base}px;
  }

  @media (max-width: 900px) {
    padding: 0;
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  height: 6rem;
  background: var(--color-primary);
  color: #fff;
  border: 0;
  border-radius: 12px;
  font-family: Archivo, sans-serif;
  font-weight: var(--font-weight-semibold);
  font-size: ${fontSizes.base}px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 3.2rem;
  box-shadow: 0 8px 24px rgba(70, 20, 187, 0.2);

  &:hover {
    background: var(--color-primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(70, 20, 187, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const GoogleButton = styled.button`
  width: 100%;
  height: 6rem;
  background: #ffffff;
  color: var(--color-text-title);
  border: 2px solid #eaecef;
  border-radius: 12px;
  font-family: Archivo, sans-serif;
  font-weight: var(--font-weight-medium);
  font-size: ${fontSizes.base}px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 1.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);

  &:hover {
    background: #fafbfc;
    border-color: #d1d5db;
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const CardFooter = styled.footer`
  margin-top: 4.8rem;
  text-align: center;
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  color: var(--color-text-complement);
  font-weight: var(--font-weight-medium);

  a {
    color: var(--color-primary);
    font-weight: var(--font-weight-bold);
    text-decoration: none;
    margin-left: 0.8rem;
    transition: opacity 0.2s ease;

    &:hover {
      text-decoration: underline;
      opacity: 0.8;
    }
  }
`;
